import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Plus, Building2, Mail, Phone, MessageCircle, Trash2, Pencil, ExternalLink, Loader2, Search, Send, Sparkles, Eye, EyeOff, Copy, ChevronDown, KeyRound, FileSpreadsheet, FileText, FileArchive, Wallet, Settings as SettingsIcon, Download, Flag, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { listCompanies, deleteCompany, sendCompanyTestEmail, api } from "../lib/api";
import { CompanyWizard } from "../components/CompanyWizard";
import { INVOICE_PROVIDERS } from "../lib/invoiceProviders";
// OnboardingFlowBrevo import removed — wizard component is no longer rendered
import { useAuth } from "../lib/auth";
import { cn } from "../lib/utils";
import { WorkCreditsModal } from "../components/WorkCreditsModal";
import { WorkspaceImportModal } from "../components/WorkspaceImportModal";
import { AISessionPanel } from "../components/AISessionPanel";
import { listPendingTopups, formatCredits, formatNis, creditsToNis, setFocusCompany, clearFocusCompany } from "../lib/credits";
import { PACKAGE_LABELS } from "../lib/packages";

// Re-export so older callers that import from this file still work.
export { PACKAGE_LABELS };

const CHANNEL_META = {
  whatsapp: { label: "וואטסאפ", icon: MessageCircle, color: "text-green-600 bg-green-50 border-green-200" },
  email: { label: "אימייל", icon: Mail, color: "text-blue-600 bg-blue-50 border-blue-200" },
  sms: { label: "SMS", icon: Phone, color: "text-orange-600 bg-orange-50 border-orange-200" },
};

// Customer package tiers — labels live in /app/frontend/src/lib/packages.js
// and are re-exported above for older imports.

export default function ShanyDashboard() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [testEmailCompany, setTestEmailCompany] = useState(null);
  const [testEmailTo, setTestEmailTo] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [credentialsCompany, setCredentialsCompany] = useState(null); // expanded credentials view
  const [showSecrets, setShowSecrets] = useState({}); // per-field reveal toggles
  const [pendingByCompany, setPendingByCompany] = useState({}); // {companyId: count}
  const [workModalCompany, setWorkModalCompany] = useState(null); // company opened in the WorkCreditsModal
  const [workModalTab, setWorkModalTab] = useState("session"); // initial tab when opening
  const [_fusCompany, _setFusCompany] = useState(null); // FUS removed — kept to avoid touching callers
  const [featureCompany, setFeatureCompany] = useState(null); // company opened in CompanyFeaturesModal
  const [workspaceImportOpen, setWorkspaceImportOpen] = useState(false); // portable workspace import modal
  const [deleteByEmailOpen, setDeleteByEmailOpen] = useState(false);
  const [deleteEmailInput, setDeleteEmailInput] = useState("");
  const [deletingEmail, setDeletingEmail] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null); // last API response for the dialog summary
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusedCompanyId = searchParams.get("company");
  const focusedCompany = companies.find((c) => c.id === focusedCompanyId);

  // Owner spec 2026-06-15n — SA types an email; endpoint kills the user
  // + every business tied to that email so it can register again from scratch.
  const handleDeleteByEmail = async () => {
    const email = (deleteEmailInput || "").trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("הכנס/י אימייל תקין");
      return;
    }
    if (!window.confirm(`בטוח/ה שברצונך למחוק את המשתמש ${email} + כל העסקים שלו?\n\nהפעולה אינה הפיכה.`)) return;
    setDeletingEmail(true);
    setDeleteResult(null);
    try {
      const res = await api.post(`/admin/delete-user-by-email`, { email });
      const d = res.data || {};
      setDeleteResult(d);
      const total = Object.values(d.deleted || {}).reduce((s, v) => s + (typeof v === "number" ? v : 0), 0);
      toast.success(`נמחקו ${d.deleted?.users || 0} משתמשים, ${d.deleted?.companies || 0} עסקים, סה״כ ${total} רשומות.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "המחיקה נכשלה");
    } finally {
      setDeletingEmail(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await listCompanies();
      // Pin the internal "FlowUp" pseudo-company to the FIRST card position
      // so a new client never pushes it out of sight. Preserves the
      // backend's original ordering for everything else.
      const raw = data.companies || [];
      const internal = raw.filter((c) => c.is_internal);
      const rest = raw.filter((c) => !c.is_internal);
      setCompanies([...internal, ...rest]);
    } catch {
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Auto-open WorkCreditsModal when ?open-session=<companyId> is set in
  // the URL. Used by the SessionCollisionModal "go to open session" button.
  // We strip the query param immediately after opening so the modal doesn't
  // re-pop every time `companies` reloads (which happens after balance
  // adjustments, focus toggles, top-ups, etc.).
  useEffect(() => {
    const openSessionId = searchParams.get("open-session");
    if (!openSessionId || companies.length === 0) return;
    const target = companies.find((c) => c.id === openSessionId);
    if (target) {
      setWorkModalTab("session");
      setWorkModalCompany(target);
      // Strip the query so subsequent reloads don't re-trigger.
      const next = new URLSearchParams(searchParams);
      next.delete("open-session");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, companies]);

  // For super-admins: count pending top-ups per company so cubes can show
  // a small badge. Refreshes every 30s.
  useEffect(() => {
    if (user?.role !== "super_admin") return;
    const fetchPending = async () => {
      try {
        const list = await listPendingTopups("pending");
        const map = {};
        for (const t of list) {
          const cid = t.company_id;
          if (!cid) continue;
          map[cid] = (map[cid] || 0) + 1;
        }
        setPendingByCompany(map);
      } catch {}
    };
    fetchPending();
    const t = setInterval(fetchPending, 30_000);
    return () => clearInterval(t);
  }, [user?.role]);

  const handleDelete = async (c) => {
    try {
      await deleteCompany(c.id);
      toast.success(`החברה "${c.name}" נמחקה`);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה במחיקה");
    }
  };

  const handleTestEmail = async () => {
    if (!testEmailTo.trim() || !testEmailTo.includes("@")) {
      toast.error("יש להזין כתובת אימייל תקינה");
      return;
    }
    setSendingTest(true);
    try {
      const res = await sendCompanyTestEmail(testEmailCompany.id, testEmailTo.trim());
      if (res.ok) {
        toast.success("הודעת בדיקה נשלחה בהצלחה! בדקי את תיבת הדואר", {
          description: `ID: ${res.id}`,
        });
        setTestEmailCompany(null);
        setTestEmailTo("");
      } else {
        toast.error(res.error || "שגיאה בשליחה");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בשליחה");
    } finally {
      setSendingTest(false);
    }
  };

  const filtered = companies.filter((c) =>
    !search.trim() || (c.name || "").toLowerCase().includes(search.trim().toLowerCase())
  );

  const providerLabel = (key, custom) => {
    if (key === "other") return custom || "ספק אחר";
    return INVOICE_PROVIDERS.find((p) => p.key === key)?.label || "ללא ספק";
  };

  return (
    <div dir="rtl" className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-brand-50/30">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-20 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {focusedCompany?.logo_url ? (
              <div className="h-12 w-12 rounded-xl bg-white border-2 border-brand/20 overflow-hidden flex items-center justify-center shadow-sm">
                <img src={focusedCompany.logo_url} alt={focusedCompany.name} className="h-full w-full object-contain p-1" data-testid="dashboard-company-logo" />
              </div>
            ) : (
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-brand to-brand-700 flex items-center justify-center shadow-sm">
                <Building2 className="h-5 w-5 text-white" />
              </div>
            )}
            <div>
              <h1 className="font-heading font-bold text-lg sm:text-xl">
                {focusedCompany ? (focusedCompany.display_name || focusedCompany.name) : "דשבורד ניהול חברות"}
              </h1>
              <p className="text-xs text-stone-500 hidden sm:block">
                {focusedCompany ? "ניהול הלקוחות והחיבורים שלך" : (user?.role === "owner" ? "העסק שלך, מסודר במקום אחד" : "הקמה, חיבור וניהול לקוחות של FlowUp")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user?.role !== "owner" && (
              <Link
                to="/"
                className="hidden sm:inline-flex items-center gap-1 text-xs text-stone-500 hover:text-brand transition"
                data-testid="link-to-main-site"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                לאתר הראשי
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* OnboardingFlowBrevo Wizard removed per user request — the "step
            X of 5" inline wizard is replaced by per-client dashboards
            accessed by clicking each company cube directly. */}

        {/* Action bar */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div className="flex-1 max-w-md relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              placeholder="חיפוש חברה..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pr-9"
              data-testid="search-companies"
            />
          </div>
          {user?.role === "super_admin" && (
            <div className="flex items-center gap-2 flex-wrap">
              <FocusedCompanyBadge companies={companies} />
              <button
                type="button"
                onClick={() => setWorkspaceImportOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-l from-indigo-600 to-fuchsia-600 hover:opacity-90 text-white text-xs font-extrabold shadow-md shadow-indigo-500/20 transition"
                data-testid="open-workspace-modal-btn"
                title="ייצוא / ייבוא Workspace מלא"
              >
                <FileArchive className="h-3.5 w-3.5" />
                Workspace
              </button>
              {/* Owner spec 2026-06-15n — SA types an email → we wipe the
                  user AND every business tied to it so the same address
                  can register from scratch. */}
              <button
                type="button"
                onClick={() => { setDeleteResult(null); setDeleteEmailInput(""); setDeleteByEmailOpen(true); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-l from-rose-600 to-red-700 hover:opacity-90 text-white text-xs font-extrabold shadow-md shadow-rose-500/30 transition"
                data-testid="delete-user-by-email-btn"
                title="מוחק משתמש + כל עסק שקשור לאותו אימייל"
              >
                <Trash2 className="h-3.5 w-3.5" />
                מחיקה לפי אימייל
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-brand" />
          </div>
        ) : companies.length === 0 ? (
          <EmptyState />
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-stone-500">
            לא נמצאו חברות בחיפוש
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="companies-grid">
            {filtered.map((c) => (
              <article
                key={c.id}
                onClick={(e) => {
                  // Whole-card click → open client's bespoke dashboard.
                  // Inner buttons (edit/delete/test-email) call stopPropagation
                  // so they won't fire navigation. Anchors with explicit hrefs
                  // also work normally.
                  if (e.target.closest("button, a")) return;
                  // Internal "FlowUp" pseudo-company → land on the
                  // FlowUp main dashboard (root) instead of a per-tenant
                  // slug page. Everything we change on the platform IS
                  // its dashboard.
                  if (c.is_internal) {
                    navigate("/");
                    return;
                  }
                  if (c.slug) navigate(`/${c.slug}`);
                }}
                className={cn(
                  "bg-white rounded-xl p-3.5 hover:shadow-xl hover:-translate-y-0.5 transition group cursor-pointer",
                  c.is_internal
                    ? (c.is_focused
                        ? "border-4 border-indigo-500 ring-4 ring-indigo-300/70 shadow-indigo-500/40 shadow-2xl bg-gradient-to-br from-indigo-50 to-fuchsia-50 scale-[1.02]"
                        : "border-4 border-indigo-400 ring-2 ring-indigo-300/60 shadow-lg shadow-indigo-400/30 bg-gradient-to-br from-indigo-50 to-fuchsia-50")
                    : (c.is_focused
                        ? "border-2 border-amber-400 ring-4 ring-amber-200/70 shadow-amber-200/40 shadow-lg"
                        : "border-2 border-stone-300 hover:border-brand/60 ring-1 ring-stone-200/50")
                )}
                data-testid={`company-card-${c.id}`}
              >
                {c.is_internal && (
                  <div className="-mt-2 -mx-2 mb-2 px-3 py-1.5 rounded-t-xl bg-gradient-to-l from-indigo-600 via-fuchsia-600 to-indigo-600 text-white text-[11px] font-extrabold flex items-center justify-between gap-2 shadow-md">
                    <span className="inline-flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 animate-pulse" />
                      פיתוח פנימי · FlowUp Core
                    </span>
                    <span className="bg-white/25 backdrop-blur-sm rounded-full px-2 py-0.5 text-[9px] tracking-wider">
                      ללא חיוב
                    </span>
                  </div>
                )}
                <header className="flex items-start justify-between gap-2 mb-2.5">
                  <div className="flex items-center gap-2.5 flex-1 min-w-0">
                    {c.logo_url ? (
                      <div className="h-12 w-12 sm:h-14 sm:w-14 flex items-center justify-center flex-shrink-0">
                        <img src={c.logo_url} alt={c.name} className="h-full w-full object-contain" />
                      </div>
                    ) : (
                      <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-xl bg-gradient-to-br from-brand-50 to-brand-100 flex items-center justify-center text-brand font-bold flex-shrink-0 text-xl ring-2 ring-brand-100 shadow-md">
                        {(c.name || "?").charAt(0)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-extrabold text-stone-900 text-base sm:text-lg leading-tight truncate" data-testid={`company-name-${c.id}`} title={c.name}>
                        {c.name}
                      </h3>
                      {c.contact_name && (
                        <p className="text-[11px] text-stone-600 truncate mt-0.5">{c.contact_name}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {user?.role === "super_admin" && (
                      <FocusFlagButton company={c} onToggled={load} />
                    )}
                    <div className="flex flex-col gap-0.5 opacity-60 group-hover:opacity-100 transition">
                      <button type="button"
                        onClick={() => setConfirmDelete(c)}
                        className="h-6 w-6 rounded-md hover:bg-red-50 text-stone-500 hover:text-red-600 inline-flex items-center justify-center"
                        title="מחיקה"
                        data-testid={`delete-company-${c.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                      <button type="button"
                        onClick={() => setFeatureCompany(c)}
                        className="h-6 w-6 rounded-md hover:bg-purple-50 text-stone-500 hover:text-purple-600 inline-flex items-center justify-center"
                        title="פיצ׳רים ניסיוניים"
                        data-testid={`features-company-${c.id}`}
                      >
                        <Sparkles className="h-3.5 w-3.5" />
                      </button>
                      <button type="button"
                        onClick={() => { setEditing(c); setWizardOpen(true); }}
                        className="h-6 w-6 rounded-md hover:bg-stone-100 text-stone-500 hover:text-brand inline-flex items-center justify-center"
                        title="עריכה"
                        data-testid={`edit-company-${c.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </header>

                {/* AI Session tracker — appears RIGHT under the flag/header
                    when this company is currently focused. Lets the SA log
                    Emergent credit delta + multiplier per unit of work. */}
                {user?.role === "super_admin" && c.is_focused && (
                  <AISessionPanel company={c} onChanged={load} />
                )}

                <div className="space-y-1.5 text-[11px] text-stone-600">
                  {/* Access-code row removed 2026-05-20 per owner spec —
                      no more visible access codes anywhere. Codes are
                      still generated server-side as immutable IDs, just
                      not displayed. */}
                  {c.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                      <span dir="ltr">{c.phone}</span>
                    </div>
                  )}
                </div>

                {/* Package badge — shows the customer's current plan
                    (e.g., "חבילת בסיס"). Defaults to "ללא חבילה" when not set. */}
                <div className="mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-extrabold bg-gradient-to-l from-indigo-100 to-fuchsia-100 text-indigo-700 border border-indigo-200" data-testid={`company-package-${c.id}`}>
                  📦 {PACKAGE_LABELS[c.package] || "ללא חבילה"}
                </div>

                {/* Credits mini-panel — visible only to super-admin.
                    Balance + sums + "ניהול עבודה וקרדיטים" button that opens
                    the full work/credit management modal for this company. */}
                {user?.role === "super_admin" && (
                  <div className="mt-3 rounded-xl bg-gradient-to-l from-fuchsia-50 to-indigo-50 border border-fuchsia-200/60 p-2.5" data-testid={`company-credits-${c.id}`} onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Wallet className="h-3.5 w-3.5 text-fuchsia-600 flex-shrink-0" />
                        <span className="text-[10px] font-bold text-stone-500">קרדיטים:</span>
                        <span className={`text-sm font-extrabold ${(c.credit_balance || 0) < 0 ? "text-rose-600" : (c.credit_balance || 0) < 20 ? "text-amber-600" : "text-fuchsia-700"}`} data-testid={`company-balance-${c.id}`}>
                          {formatCredits(c.credit_balance || 0)}
                        </span>
                        <span className="text-[9px] text-stone-400">({formatNis(creditsToNis(c.credit_balance || 0))})</span>
                      </div>
                      {c.active_work_session && (
                        <span className="inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-extrabold rounded-full px-2 py-0.5 animate-pulse" title="סשן עבודה פעיל" data-testid={`company-active-session-${c.id}`}>
                          ● בעבודה
                        </span>
                      )}
                    </div>
                    {/* Billing / FUS controls hidden for the internal
                        pseudo-company — it has no real customer behind
                        it and exists only as a focus target for
                        "deploy the dashboard codebase only" flows. */}
                    {!c.is_internal && (
                      <>
                        <div className="flex items-center justify-between gap-2 mb-2 text-[10px]">
                          <span className="text-stone-500">סך נרכש: <strong className="text-emerald-700">{formatCredits(c.total_topped_up || 0)}</strong></span>
                          <span className="text-stone-500">סך חויב: <strong className="text-rose-700">{formatCredits(c.total_ai_cost_charged || 0)}</strong></span>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setWorkModalTab("session"); setWorkModalCompany(c); }}
                          className="w-full inline-flex items-center justify-center gap-1.5 bg-gradient-to-l from-fuchsia-600 to-indigo-600 hover:opacity-90 text-white text-xs font-extrabold px-3 py-2 rounded-lg shadow-md shadow-fuchsia-500/20 transition"
                          data-testid={`work-credits-btn-${c.id}`}
                        >
                          <SettingsIcon className="h-3.5 w-3.5" /> ניהול עבודה וקרדיטים
                          {pendingByCompany[c.id] > 0 && (
                            <span className="bg-amber-400 text-stone-900 text-[10px] font-extrabold rounded-full px-1.5 py-0.5">{pendingByCompany[c.id]}</span>
                          )}
                        </button>
                      </>
                    )}
                    {c.is_internal && (
                      <div className="rounded-lg bg-indigo-100 border border-indigo-300 text-indigo-700 text-[11px] font-bold px-2.5 py-2 text-center">
                        סמן/י את הדגל הצהוב למעלה כדי לדפלוי רק על דשבורד FlowUp
                      </div>
                    )}
                    {/* FlowUpSender per-company button removed per user
                        request — opens are now done via "פתח דשבורד" only. */}
                  </div>
                )}

                <footer className="mt-4 pt-3 border-t border-stone-100 flex items-center justify-end gap-2">
                  <div className="flex items-center gap-1.5">
                    {c.is_internal ? (
                      // FlowUp internal card → footer button opens the
                      // FlowUp main dashboard (root). No per-tenant route.
                      <Link to="/" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-[10px] font-bold bg-gradient-to-l from-indigo-600 to-fuchsia-600 hover:opacity-90 text-white px-2 py-1 rounded-md transition flex-shrink-0"
                          data-testid={`open-flowup-main-${c.id}`}
                        >
                          <ExternalLink className="h-3 w-3" />
                          פתח דשבורד FlowUp
                        </button>
                      </Link>
                    ) : c.slug && (
                      <Link to={`/${c.slug}`} onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-[10px] font-bold bg-gradient-to-l from-fuchsia-600 to-emerald-600 hover:opacity-90 text-white px-2 py-1 rounded-md transition flex-shrink-0"
                          data-testid={`open-client-dashboard-${c.id}`}
                        >
                          <ExternalLink className="h-3 w-3" />
                          פתח דשבורד
                        </button>
                      </Link>
                    )}
                    {(c.channels || []).includes("email") && false && (
                      <button type="button"
                        onClick={(e) => { e.stopPropagation(); setTestEmailCompany(c); setTestEmailTo(""); }}
                        className="inline-flex items-center gap-1 text-[10px] font-bold bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded-md transition flex-shrink-0"
                        data-testid={`test-email-${c.id}`}
                      >
                        <Send className="h-3 w-3" />
                        בדיקה
                      </button>
                    )}
                  </div>
                </footer>
              </article>
            ))}
          </div>
        )}
      </main>

      <CompanyWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        editing={editing}
        onSaved={async () => { setWizardOpen(false); await load(); }}
      />

      {testEmailCompany && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setTestEmailCompany(null)}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
            data-testid="test-email-dialog"
          >
            <h3 className="font-heading font-bold mb-1 flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              שליחת אימייל בדיקה
            </h3>
            <p className="text-xs text-stone-500 mb-4">
              מאת: <strong>{testEmailCompany.name}</strong>
            </p>
            <label className="block">
              <span className="text-xs font-semibold">שלחי אל (האימייל שלך):</span>
              <Input
                type="email"
                dir="ltr"
                value={testEmailTo}
                onChange={(e) => setTestEmailTo(e.target.value)}
                placeholder="you@example.com"
                className="mt-1 text-right"
                autoFocus
                data-testid="test-email-to-input"
              />
            </label>
            <div className="flex gap-2 flex-row-reverse mt-4">
              <Button
                onClick={handleTestEmail}
                disabled={sendingTest}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="send-test-email-button"
              >
                {sendingTest ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <><Send className="h-4 w-4 ml-2" />שלחי עכשיו</>
                )}
              </Button>
              <Button variant="outline" onClick={() => setTestEmailCompany(null)}>
                ביטול
              </Button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
          >
            <h3 className="font-heading font-bold mb-2">מחיקת חברה</h3>
            <p className="text-sm text-stone-600 mb-4">
              האם למחוק את החברה <strong>{confirmDelete.name}</strong>? הפעולה אינה הפיכה.
            </p>
            <div className="flex gap-2 flex-row-reverse">
              <Button onClick={() => handleDelete(confirmDelete)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-company">
                <Trash2 className="h-4 w-4 ml-2" />
                כן, מחק
              </Button>
              <Button variant="outline" onClick={() => setConfirmDelete(null)}>
                ביטול
              </Button>
            </div>
          </div>
        </div>
      )}

      <Toaster richColors position="top-center" />

      {/* Super-admin work + credits popup, opened from each cube */}
      {workModalCompany && (
        <WorkCreditsModal
          company={workModalCompany}
          defaultTab={workModalTab}
          onClose={() => setWorkModalCompany(null)}
          onChanged={load}
        />
      )}

      {/* Per-company feature flags */}
      {featureCompany && (
        <CompanyFeaturesModal
          company={featureCompany}
          onClose={() => setFeatureCompany(null)}
          onSaved={load}
        />
      )}

      {/* FLOW UP SENDER sidebar — REMOVED (FlowUp products wiped). */}

      {/* Portable Workspace Import — ZIP-based system-wide restore. */}
      <WorkspaceImportModal
        open={workspaceImportOpen}
        onOpenChange={setWorkspaceImportOpen}
        onImported={load}
      />

      {/* Owner spec 2026-06-15n — Delete-user-by-email dialog. */}
      <DeleteUserByEmailDialog
        open={deleteByEmailOpen}
        onOpenChange={setDeleteByEmailOpen}
        email={deleteEmailInput}
        setEmail={setDeleteEmailInput}
        onSubmit={handleDeleteByEmail}
        loading={deletingEmail}
        result={deleteResult}
      />
    </div>
  );
}

// ---------- Delete-User-by-Email dialog ----------
function DeleteUserByEmailDialog({ open, onOpenChange, email, setEmail, onSubmit, loading, result }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="sm:max-w-md" data-testid="delete-user-by-email-dialog">
        <DialogHeader>
          <DialogTitle className="text-right flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-rose-500" />
            מחיקה לפי אימייל
          </DialogTitle>
          <DialogDescription className="text-right text-stone-500 text-xs">
            הכנס/י אימייל — הפעולה תמחק את המשתמש ואת כל העסקים המשויכים
            אליו, כך שיוכל להירשם מחדש עם אותו אימייל. סופר-אדמינים ו-Full
            Frame מוגנים ולא יימחקו.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-1">
          <Input
            type="email"
            dir="ltr"
            placeholder="user@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            className="text-left"
            data-testid="delete-user-by-email-input"
          />
          {result && (
            <div className="rounded-lg border border-emerald-500/40 bg-emerald-50 p-3 text-xs text-emerald-900 space-y-1" data-testid="delete-user-by-email-result">
              <div className="font-bold">בוצע ✓</div>
              <div>משתמשים שנמחקו: <b>{result.deleted?.users || 0}</b></div>
              <div>עסקים שנמחקו: <b>{result.deleted?.companies || 0}</b></div>
              {result.killed_companies?.length > 0 && (
                <ul className="pt-1 space-y-0.5">
                  {result.killed_companies.map((c) => (
                    <li key={c.id} className="truncate">• {c.name}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <div className="flex items-center gap-2 justify-end pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              data-testid="delete-user-by-email-cancel"
            >
              ביטול
            </Button>
            <Button
              type="button"
              onClick={onSubmit}
              disabled={loading || !email.trim()}
              className="bg-rose-600 hover:bg-rose-700 text-white font-bold gap-1.5"
              data-testid="delete-user-by-email-confirm"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              מחק כל הקמת עסק ונתונים
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EmptyState() {
  return (
    <div className="bg-white border border-dashed border-stone-300 rounded-2xl p-12 text-center" data-testid="empty-state">
      <div className="h-16 w-16 rounded-full bg-brand-50 flex items-center justify-center mx-auto mb-4">
        <Building2 className="h-8 w-8 text-brand" />
      </div>
      <h3 className="font-heading font-bold text-xl mb-2">אין עדיין חברות</h3>
      <p className="text-sm text-stone-500 mb-2 max-w-sm mx-auto">
        חברות יופיעו כאן אוטומטית ברגע שלקוחות נרשמים בעצמם דרך כפתור
        "בנה אוטומציה חדשה" בדף הבית.
      </p>
      <a
        href="/"
        className="inline-block text-xs font-bold text-brand hover:underline mt-2"
        data-testid="empty-state-home-link"
      >
        ← מעבר לדף הבית
      </a>
    </div>
  );
}

// =============================================================================
// CredentialsVault — expandable per-company panel showing every API key & secret
// for the customer's 3rd-party integrations (Brevo, Green Invoice, Google Sheet).
// =============================================================================
function CredentialsVault({ company, showSecrets, setShowSecrets }) {
  const c = company;
  const toggle = (k) => setShowSecrets((s) => ({ ...s, [k]: !s[k] }));
  const copy = async (label, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} הועתק`);
    } catch { toast.error("לא ניתן להעתיק"); }
  };

  const sections = [
    {
      key: "brevo",
      title: "Brevo (Email + WhatsApp + SMS)",
      icon: Sparkles,
      color: "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-900",
      fields: [
        { k: "brevo_api_key", label: "API Key", value: c.brevo_api_key, secret: true },
        { k: "brevo_from_email", label: "Email From", value: c.brevo_from_email, secret: false },
        { k: "brevo_whatsapp_from", label: "WhatsApp From", value: c.brevo_whatsapp_from, secret: false },
        { k: "brevo_sms_sender_id", label: "SMS Sender ID", value: c.brevo_sms_sender_id, secret: false },
      ],
    },
    {
      key: "invoice",
      title: "חשבונית ירוקה",
      icon: FileText,
      color: "bg-blue-50 border-blue-200 text-blue-900",
      fields: [
        { k: "invoice_api_key", label: "API Key", value: c.invoice_api_key, secret: true },
        { k: "invoice_api_secret", label: "API Secret", value: c.invoice_api_secret, secret: true },
        { k: "invoice_provider", label: "ספק", value: c.invoice_provider || "—", secret: false },
      ],
    },
    {
      key: "sheet",
      title: "Google Sheets",
      icon: FileSpreadsheet,
      color: "bg-emerald-50 border-emerald-200 text-emerald-900",
      fields: [
        { k: "gsheet_url", label: "Sheet URL", value: c.gsheet_url, secret: false, link: true },
      ],
    },
    {
      key: "contact",
      title: "פרטי הלקוח",
      icon: Building2,
      color: "bg-stone-50 border-stone-200 text-stone-900",
      fields: [
        { k: "contact_name", label: "איש קשר", value: c.contact_name, secret: false },
        { k: "email", label: "אימייל", value: c.email, secret: false },
        { k: "phone", label: "טלפון", value: c.phone, secret: false },
        { k: "slug", label: "URL Slug", value: c.slug, secret: false },
      ],
    },
  ];

  const mask = (v) => (v ? "•".repeat(Math.min(v.length, 20)) : "");

  return (
    <div className="mt-2 space-y-2 bg-stone-50/50 rounded-xl border border-stone-200 p-3" data-testid={`credentials-vault-${c.id}`}>
      {sections.map((s) => {
        const Icon = s.icon;
        return (
          <div key={s.key} className={cn("rounded-lg border p-2.5", s.color)} data-testid={`vault-section-${s.key}-${c.id}`}>
            <div className="flex items-center gap-1.5 mb-1.5">
              <Icon className="h-3.5 w-3.5" />
              <span className="text-[11px] font-bold">{s.title}</span>
            </div>
            <div className="space-y-1">
              {s.fields.map((f) => {
                const empty = !f.value;
                const masked = f.secret && !showSecrets[`${c.id}-${f.k}`];
                const display = empty ? "— לא הוגדר —" : (masked ? mask(f.value) : f.value);
                return (
                  <div key={f.k} className="flex items-center justify-between gap-2 bg-white rounded-md px-2 py-1.5 border border-current/20">
                    <span className="text-[10px] font-bold text-stone-500 shrink-0 w-24">{f.label}</span>
                    <span
                      dir="ltr"
                      className={cn(
                        "flex-1 font-mono text-[11px] truncate text-right",
                        empty ? "text-stone-400 italic" : "text-stone-800"
                      )}
                      title={empty ? "" : f.value}
                    >
                      {f.link && !empty ? (
                        <a href={f.value} target="_blank" rel="noreferrer" className="underline hover:text-emerald-700">
                          {f.value}
                        </a>
                      ) : (
                        display
                      )}
                    </span>
                    {!empty && (
                      <div className="flex items-center gap-0.5 shrink-0">
                        {f.secret && (
                          <button
                            type="button"
                            onClick={() => toggle(`${c.id}-${f.k}`)}
                            className="h-6 w-6 rounded hover:bg-stone-100 inline-flex items-center justify-center text-stone-500"
                            title={masked ? "הצג" : "הסתר"}
                            data-testid={`reveal-${f.k}-${c.id}`}
                          >
                            {masked ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => copy(f.label, f.value)}
                          className="h-6 w-6 rounded hover:bg-stone-100 inline-flex items-center justify-center text-stone-500"
                          title="העתק"
                          data-testid={`copy-${f.k}-${c.id}`}
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}



// ============== Focus Flag (AI agent target marker) ==============
//
// Marking a company's flag tells the AI agent that subsequent code
// changes / deploys / billed work apply ONLY to this company. Only one
// company can be focused platform-wide. Clicking again clears it.
function FocusFlagButton({ company, onToggled }) {
  const [busy, setBusy] = useState(false);
  const [collision, setCollision] = useState(null);
  const focused = !!company.is_focused;

  const toggle = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      if (focused) {
        await clearFocusCompany(company.id);
        toast.success(`הסרת פוקוס מ-${company.display_name || company.name}`);
      } else {
        await setFocusCompany(company.id);
        toast.success(`פוקוס נקבע: ${company.display_name || company.name}`, {
          description: "כל שינוי קוד / סשן / דפלוי יבוצע רק על לקוח זה",
        });
      }
      onToggled?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && typeof detail === "object") {
        // Same single-session collision modal as work-session/start.
        // Tells SA which client currently holds the session, with a CTA
        // to jump straight there and close it.
        setCollision({
          blocker_name: detail.blocking_company?.name || "לקוח אחר",
          blocker_id: detail.blocking_company?.id,
          requested_name: detail.requested_company?.name || (company.display_name || company.name),
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "שגיאה בקביעת פוקוס");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      title={focused ? "לחץ כדי להסיר פוקוס" : "סמן כפוקוס לעבודה (סשן / דפלוי)"}
      data-testid={`focus-flag-${company.id}`}
      className={cn(
        "relative h-9 w-9 rounded-lg inline-flex items-center justify-center transition flex-shrink-0",
        focused
          ? "bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg shadow-amber-500/40 ring-2 ring-amber-200 animate-pulse"
          : "bg-stone-100 text-stone-400 hover:bg-amber-50 hover:text-amber-500 ring-1 ring-stone-200"
      )}
    >
      <Flag className={cn("h-5 w-5", focused && "fill-white")} />
      {focused && (
        <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald-500 border-2 border-white">
        </span>
      )}
    </button>
    {collision && (
      <FocusCollisionModal
        collision={collision}
        onClose={() => setCollision(null)}
        onGoToBlocker={() => {
          setCollision(null);
          // Navigate to /S.ADMIN with the blocker's session pre-opened —
          // reuses the same `?open-session=<id>` flow as the credits modal.
          window.location.href = `/S.ADMIN?open-session=${collision.blocker_id}`;
        }}
      />
    )}
    </>
  );
}

// ============== Focus Collision Modal ==============
//
// Shown when SA tries to flag company B while company A still has an
// open work session. Visually mirrors the SessionCollisionModal in
// WorkCreditsModal so the UX feels consistent platform-wide.
function FocusCollisionModal({ collision, onClose, onGoToBlocker }) {
  return (
    <div
      className="fixed inset-0 z-[10003] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
        data-testid="focus-collision-modal"
        className="bg-gradient-to-br from-stone-900 to-rose-950/40 border-2 border-amber-500/60 rounded-2xl shadow-2xl shadow-amber-500/20 max-w-md w-full overflow-hidden"
      >
        <div className="bg-gradient-to-l from-amber-500/20 to-rose-500/20 px-5 py-3 border-b border-amber-500/40 flex items-center gap-2">
          <div className="h-9 w-9 rounded-full bg-amber-500/30 flex items-center justify-center">
            <Flag className="h-5 w-5 text-amber-300" />
          </div>
          <h3 className="font-heading text-base font-extrabold text-amber-100">לא ניתן להעביר את הדגל</h3>
        </div>
        <div className="px-5 py-4 space-y-3 text-stone-200">
          <p className="text-sm leading-relaxed">
            יש לך כבר סשן עבודה פתוח עבור
            <span className="mx-1 inline-flex items-center gap-1 rounded-full bg-amber-500/20 border border-amber-400/40 px-2 py-0.5 text-amber-100 font-extrabold text-xs">
              {collision.blocker_name}
            </span>
          </p>
          <p className="text-sm text-stone-300 leading-relaxed">
            נא לסגור אותו לפני העברת הדגל אל
            <span className="mx-1 inline-flex items-center gap-1 rounded-full bg-fuchsia-500/20 border border-fuchsia-400/40 px-2 py-0.5 text-fuchsia-100 font-extrabold text-xs">
              {collision.requested_name}
            </span>
          </p>
        </div>
        <div className="px-5 py-3 bg-stone-950/60 border-t border-white/5 flex gap-2 flex-row-reverse">
          <button
            type="button"
            onClick={onGoToBlocker}
            className="flex-1 bg-gradient-to-l from-amber-500 to-orange-500 hover:opacity-90 text-stone-900 font-extrabold py-2.5 rounded-lg inline-flex items-center justify-center gap-2 transition shadow-lg shadow-amber-500/30"
            data-testid="focus-goto-blocker-btn"
          >
            עבור לסשן הפתוח →
          </button>
          <button
            type="button"
            onClick={onClose}
            className="bg-stone-800 hover:bg-stone-700 text-stone-300 text-sm font-bold px-4 py-2.5 rounded-lg"
            data-testid="focus-dismiss-collision-btn"
          >
            סגור
          </button>
        </div>
      </div>
    </div>
  );
}

// ============== Focused Company Badge (header pill) ==============
//
// Always-visible pill in the action bar showing the currently focused
// company, so the super-admin / AI agent never lose track of who they
// are working on. Empty state shows a neutral "ללא פוקוס" indicator.
function FocusedCompanyBadge({ companies }) {
  const focused = (companies || []).find((c) => c.is_focused);
  if (!focused) {
    return (
      <div
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold bg-stone-100 text-stone-500 border border-stone-200"
        data-testid="focused-company-badge-empty"
      >
        <Flag className="h-3.5 w-3.5" />
        ללא פוקוס — שינויים כלליים
      </div>
    );
  }
  return (
    <div
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-extrabold bg-gradient-to-l from-amber-400 to-orange-500 text-white border border-amber-300 shadow-md shadow-amber-500/30"
      data-testid="focused-company-badge"
      title="הסוכן עובד כרגע רק על הלקוח הזה"
    >
      <Flag className="h-3.5 w-3.5 fill-white" />
      פוקוס: {focused.display_name || focused.name}
    </div>
  );
}

// ============== Import from Production (one-click sync) ==============
//
// Stores the source credentials server-side (env vars) so the admin just
// clicks once — no password prompt. Mirrors ALL company metadata (name,
// logo, package, brand color, channels). Financial state is never copied.
function ImportFromProductionButton({ onImported }) {
  const [busy, setBusy] = useState(false);

  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/sync-from-production", {});
      toast.success(`סנכרון הושלם: ${data.imported} חדשות, ${data.updated} עודכנו (סה״כ ${data.total_seen})`);
      if (typeof onImported === "function") onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail || "סנכרון נכשל");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      onClick={onClick}
      disabled={busy}
      className="border-brand/30 hover:bg-brand/5"
      data-testid="import-from-production-btn"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin ml-1.5" /> : <Download className="h-4 w-4 ml-1.5" />}
      סנכרן מפרודקשן
    </Button>
  );
}

// ============== Feature Flags Modal (super-admin tool) ==============
//
// Per-company automations management. Shows three sections:
//   1. Active automations on this client (with details + when each was enabled)
//   2. History log — full audit trail of toggle events
//   3. Add new automation — pick from the catalog
//
// Backend: registry at /api/features/registry; per-company state stored on
// the company doc in `experimental_features`; audit log at
// /api/companies/:id/features/audit.
export function CompanyFeaturesModal({ company, onClose, onSaved }) {
  const [registry, setRegistry] = useState([]);
  const [enabled, setEnabled] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [reg, hist] = await Promise.all([
          api.get("/features/registry"),
          api.get(`/companies/${company.id}/features/audit`).catch(() => ({ data: { events: [] } })),
        ]);
        if (cancelled) return;
        setRegistry(reg.data.features || []);
        setEnabled(Array.isArray(company?.experimental_features) ? company.experimental_features : []);
        setAudit(hist.data.events || []);
      } catch (e) {
        toast.error("טעינת האוטומציות נכשלה");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [company?.id, company?.experimental_features]);

  const toggle = (key) => {
    setEnabled((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/companies/${company.id}`, { experimental_features: enabled });
      toast.success("האוטומציות נשמרו");
      if (typeof onSaved === "function") onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שמירה נכשלה");
    } finally {
      setSaving(false);
    }
  };

  if (!company) return null;

  // Last "enabled=true" timestamp per feature for the "active" section.
  const enabledAt = (key) => {
    const ev = audit.find((e) => e.feature_key === key && e.enabled);
    return ev ? ev.at : null;
  };
  const fmtIL = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  const activeFeatures = registry.filter((f) => enabled.includes(f.key));
  const availableFeatures = registry.filter((f) => !enabled.includes(f.key));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !saving && onClose()} data-testid="company-features-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6 space-y-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div>
          <h2 className="font-heading font-bold text-lg flex items-center gap-2">
            <Flag className="h-5 w-5 text-brand" />
            אוטומציות — {company.name}
          </h2>
          <p className="text-xs text-stone-500 mt-1">
            ניהול האוטומציות הפעילות אצל הלקוח · היסטוריה מלאה של פעולותיך.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin text-brand inline" /></div>
        ) : (
          <>
            {/* ===== Active automations on THIS client ===== */}
            <section>
              <h3 className="text-sm font-extrabold text-stone-900 mb-2 flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-600" />
                פעיל עכשיו ({activeFeatures.length})
              </h3>
              {activeFeatures.length === 0 ? (
                <p className="text-xs text-stone-500 bg-stone-50 rounded-lg p-3">עדיין לא הופעלה אוטומציה ללקוח זה.</p>
              ) : (
                <div className="space-y-2">
                  {activeFeatures.map((f) => (
                    <ActiveFeatureCard
                      key={f.key}
                      feature={f}
                      company={company}
                      enabledAt={enabledAt(f.key)}
                      onRemove={() => toggle(f.key)}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* ===== Available (not yet enabled) ===== */}
            {availableFeatures.length > 0 && (
              <section>
                <h3 className="text-sm font-extrabold text-stone-900 mb-2">
                  זמין להוספה
                </h3>
                <div className="space-y-2">
                  {availableFeatures.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => toggle(f.key)}
                      className="w-full text-right p-3 rounded-xl border-2 border-stone-200 hover:border-brand/40 bg-white transition flex items-start gap-3"
                      data-testid={`feature-toggle-${f.key}`}
                    >
                      <div className="h-5 w-5 rounded-md border-2 border-stone-300 bg-white flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm text-stone-900">{f.label}</div>
                        <div className="text-xs text-stone-500 mt-0.5">{f.description}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* ===== Audit log ===== */}
            {audit.length > 0 && (
              <section>
                <h3 className="text-sm font-extrabold text-stone-900 mb-2">
                  היסטוריה ({audit.length})
                </h3>
                <div className="max-h-40 overflow-y-auto rounded-lg border border-stone-200 bg-stone-50/50 divide-y divide-stone-200">
                  {audit.map((ev) => {
                    const f = registry.find((x) => x.key === ev.feature_key);
                    return (
                      <div key={ev.id} className="px-3 py-2 text-[11px] flex items-center justify-between gap-2">
                        <span className="text-stone-700 truncate">
                          <span className={ev.enabled ? "text-emerald-700 font-bold" : "text-rose-700 font-bold"}>
                            {ev.enabled ? "✓ הופעל" : "× כובה"}
                          </span>{" "}
                          {f?.label || ev.feature_key}
                        </span>
                        <span className="text-stone-500 tabular-nums shrink-0">{fmtIL(ev.at)}</span>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )}

        <div className="flex gap-2 justify-end pt-3 border-t border-stone-100">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="features-cancel">ביטול</Button>
          <Button onClick={onSave} disabled={saving || loading} className="bg-brand hover:bg-brand-700" data-testid="features-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin ml-1.5" /> : <Check className="h-4 w-4 ml-1.5" />}
            שמור
          </Button>
        </div>
      </div>
    </div>
  );
}


// ============== All Features Browser ==============
//
// One-stop view of every experimental feature in the platform. For each
// feature the admin can pick a company from a dropdown and toggle it on/off
// with a single approve click. Each feature row also expands to reveal the
// description so the admin remembers what the flag does.
function AllFeaturesButton({ companies, onChanged }) {
  const [open, setOpen] = useState(false);
  const [registry, setRegistry] = useState([]);
  const [loading, setLoading] = useState(false);
  // map of {feature_key: {expanded, selected_company_id, busy}}
  const [rowState, setRowState] = useState({});

  const loadRegistry = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/features/registry");
      setRegistry(data.features || []);
    } catch (e) {
      toast.error("טעינת מאגר פיצ׳רים נכשלה");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadRegistry();
  }, [open]);

  const setRow = (key, patch) => {
    setRowState((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const toggleForCompany = async (featureKey, companyId, enable) => {
    if (!companyId) {
      toast.error("בחר חברה");
      return;
    }
    setRow(featureKey, { busy: true });
    try {
      await api.post("/admin/feature-toggle", {
        company_id: companyId,
        feature_key: featureKey,
        enabled: enable,
      });
      toast.success(enable ? "הפיצ׳ר הופעל לחברה" : "הפיצ׳ר בוטל לחברה");
      if (typeof onChanged === "function") onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "פעולה נכשלה");
    } finally {
      setRow(featureKey, { busy: false });
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(true)}
        className="border-purple-500/30 hover:bg-purple-50 text-purple-700"
        data-testid="all-features-btn"
      >
        <Flag className="h-4 w-4 ml-1.5" />
        כל האוטומציות
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="all-features-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-heading font-bold text-xl flex items-center gap-2">
                  <Flag className="h-5 w-5 text-purple-600" />
                  כל האוטומציות
                </h2>
                <p className="text-xs text-stone-500 mt-1">
                  לחצי על אוטומציה לפירוט מלא · בחרי חברה · לחצי אישור — האוטומציה תופעל רק עבורה.
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="text-stone-400 hover:text-stone-600 p-1" data-testid="all-features-close">
                <span className="text-2xl leading-none">×</span>
              </button>
            </div>

            {loading ? (
              <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin text-purple-600 inline" /></div>
            ) : (
              <div className="space-y-3">
                {registry.map((f) => {
                  const rs = rowState[f.key] || {};
                  const expanded = !!rs.expanded;
                  const selectedId = rs.selected_company_id || "";
                  const selectedCompany = companies.find((c) => c.id === selectedId);
                  const isEnabledForSelected = selectedCompany && Array.isArray(selectedCompany.experimental_features) && selectedCompany.experimental_features.includes(f.key);
                  const enabledCount = companies.filter((c) => Array.isArray(c.experimental_features) && c.experimental_features.includes(f.key)).length;
                  return (
                    <div key={f.key} className="border-2 border-stone-200 rounded-xl bg-white" data-testid={`all-features-row-${f.key}`}>
                      {/* HEADER */}
                      <div className="flex items-center gap-3 p-3">
                        <button
                          type="button"
                          onClick={() => setRow(f.key, { expanded: !expanded })}
                          className="h-7 w-7 rounded-full bg-purple-50 hover:bg-purple-100 text-purple-700 inline-flex items-center justify-center transition"
                          data-testid={`all-features-expand-${f.key}`}
                        >
                          <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm text-stone-900 truncate">{f.label}</div>
                          <div className="text-[10px] text-stone-400 mt-0.5 font-mono truncate">{f.key}</div>
                        </div>
                        {enabledCount > 0 && (
                          <span className="text-[10px] font-bold text-purple-700 bg-purple-100 border border-purple-200 rounded-full px-2 py-0.5 shrink-0">
                            פעיל ב-{enabledCount} חברות
                          </span>
                        )}
                        <div className="flex items-center gap-2 shrink-0">
                          <select
                            value={selectedId}
                            onChange={(e) => setRow(f.key, { selected_company_id: e.target.value })}
                            className="text-xs border border-stone-300 rounded-lg px-2 py-1.5 bg-white max-w-[180px]"
                            data-testid={`all-features-company-${f.key}`}
                          >
                            <option value="">בחר חברה...</option>
                            {companies.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}{Array.isArray(c.experimental_features) && c.experimental_features.includes(f.key) ? " ✓" : ""}
                              </option>
                            ))}
                          </select>
                          {selectedCompany && (
                            <Button
                              size="sm"
                              onClick={() => toggleForCompany(f.key, selectedId, !isEnabledForSelected)}
                              disabled={rs.busy}
                              className={isEnabledForSelected ? "bg-red-600 hover:bg-red-700 text-white" : "bg-purple-600 hover:bg-purple-700 text-white"}
                              data-testid={`all-features-toggle-${f.key}`}
                            >
                              {rs.busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (isEnabledForSelected ? "בטל" : "אישור")}
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* DESCRIPTION */}
                      {expanded && (
                        <div className="px-4 pb-3 pt-1 border-t border-stone-100 bg-stone-50/50">
                          <p className="text-sm text-stone-700">{f.description}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
                {registry.length === 0 && (
                  <p className="text-center text-stone-500 text-sm py-6">אין פיצ׳רים רשומים במאגר.</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}


/**
 * One row inside the "פעיל עכשיו" section of CompanyFeaturesModal.
 *
 * Renders the basic info (name, description, key, enabled-on date) and a
 * "פירוט הליך" button that expands into a textarea editor. The textarea
 * persists to /api/companies/:id/features/:key/note so each automation
 * gets a per-client playbook the admin can edit & track.
 *
 * Saving the note logs an audit entry too — so the modal's history feed
 * shows "✏️ עודכן פירוט: <feature>" alongside enable/disable events.
 */
function ActiveFeatureCard({ feature: f, company, enabledAt, onRemove }) {
  const [expanded, setExpanded] = useState(false);
  const [content, setContent] = useState("");
  const [savedContent, setSavedContent] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  // Lazy-load the note only when the user expands the card — avoids 10
  // parallel requests on modal open.
  useEffect(() => {
    if (!expanded || loaded) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/companies/${company.id}/features/${f.key}/note`);
        if (cancelled) return;
        setContent(data?.content || "");
        setSavedContent(data?.content || "");
        setUpdatedAt(data?.updated_at || null);
      } catch { /* fall through with empty */ }
      finally { if (!cancelled) setLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, [expanded, loaded, company.id, f.key]);

  const dirty = content !== savedContent;
  const fmtIL = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/companies/${company.id}/features/${f.key}/note`, { content });
      setSavedContent(data?.content || "");
      setUpdatedAt(data?.updated_at || new Date().toISOString());
      toast.success(`עודכן הליך: ${f.label}`, { description: company.name });
    } catch (e) {
      toast.error(e.response?.data?.detail || "שמירה נכשלה");
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/40 p-3" data-testid={`active-automation-${f.key}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="font-bold text-sm text-stone-900">{f.label}</div>
        <button
          type="button"
          onClick={onRemove}
          className="text-[10px] font-bold text-rose-600 hover:text-rose-700"
          data-testid={`disable-${f.key}`}
        >
          הסר
        </button>
      </div>
      <p className="text-xs text-stone-600 leading-snug">{f.description}</p>
      <div className="mt-2 text-[10px] text-stone-500 flex items-center gap-3 flex-wrap">
        <span>📅 הופעל: {fmtIL(enabledAt)}</span>
        {updatedAt && <span>✏️ עודכן: {fmtIL(updatedAt)}</span>}
        <span className="font-mono opacity-50">{f.key}</span>
      </div>

      <div className="mt-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-[11px] font-bold text-emerald-700 hover:text-emerald-900"
          data-testid={`process-toggle-${f.key}`}
        >
          {expanded ? "▲ סגור פירוט הליך" : "▼ פירוט הליך (לעריכה)"}
        </button>
        {expanded && (
          <div className="mt-2 space-y-2" data-testid={`process-editor-${f.key}`}>
            {!loaded ? (
              <div className="text-center py-3 text-xs text-stone-400"><Loader2 className="inline h-4 w-4 animate-spin" /> טוען...</div>
            ) : (
              <>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder={`איך מותאמת אוטומציה זו עבור ${company.name}?\nלמשל: הקטגוריות שהוגדרו, הגדרות מיוחדות, הערות תפעוליות...`}
                  className="w-full min-h-[140px] rounded-lg border-2 border-stone-200 bg-white p-2 text-sm leading-relaxed focus:outline-none focus:border-emerald-400 resize-y"
                  data-testid={`process-textarea-${f.key}`}
                />
                <div className="flex items-center justify-end gap-2">
                  {dirty && (
                    <button
                      type="button"
                      onClick={() => setContent(savedContent)}
                      className="text-[11px] font-bold text-stone-500 hover:text-stone-700"
                    >
                      ביטול
                    </button>
                  )}
                  <Button
                    onClick={onSave}
                    disabled={saving || !dirty}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 h-7 text-[11px]"
                    data-testid={`process-save-${f.key}`}
                  >
                    {saving ? <Loader2 className="h-3 w-3 animate-spin ml-1" /> : <Check className="h-3 w-3 ml-1" />}
                    {dirty ? "שמור עדכון" : "אין שינויים"}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
